import React, { useMemo, useState } from 'react';
import { PanelType } from './SidebarNav';
import { useSidebarNavContext } from './SidebarNavProvider';
import { useSelector } from 'react-redux';
import { getInitials } from '../../helpers/intials';
import {
  ListGroupButtonItem,
  ListGroupNavItem,
  SidebarNavSection,
} from './ListGroupNavComponents';
import {
  useSitrepLandingFlag,
  useVelcoFlag,
  useNotificationCenterFlag,
  useHighSecurityFlag,
  useHwcgFlag,
  useAorTabFlag,
  useHubOnlyFlag,
  useUscgFocRequirementsFlag,
} from '../../utils/featureFlags';
import { UPGRADE_TO_ENABLE } from 'components/Notifications/LockedUpgradeOption';
import { useDisplayGroupName } from '../../utils/epaDemoOrgDisplay';
import { useSubscriptionLevel } from '../SubscriptionManagement/useSubscriptionLevel';
import {
  getHubNavItems,
  isHubNavItemActive,
} from 'components/MapPage/hubNavTabs';
import { Folder, ListChecks, X } from 'lucide-react';
import useIsOrgAdmin from 'hooks/useIsOrgAdmin';
import {
  ONBOARDING_HREF,
  ONBOARDING_PARAM,
} from 'components/Onboarding/onboardingModel';
import { useOrgOnboardingProgress } from 'components/Onboarding/useOrgOnboarding';
import { Button } from '@/components/ui/button';
import { useAorText } from '../../utils/aorTerminology';
import { filterNavItemsByQuery, matchesNavQuery } from './sidebarNavSearch';
import { SidebarNavSearchInput } from './SidebarNavSearchInput';

const MainNavPanel: React.FC<{
  setSecondPanel: (key: PanelType) => void;
}> = ({ setSecondPanel }) => {
  const velcoFlag = useVelcoFlag();
  const user = useSelector((state: any) => state.app.user);
  const sitrepLandingFlag = useSitrepLandingFlag();
  const hideNotifications = useNotificationCenterFlag();
  const hideActions = useHighSecurityFlag();
  const hwcg = useHwcgFlag();
  const hideContactLists = !hwcg;
  const hideReviewQueue = !hwcg;
  const uscgFoc = useUscgFocRequirementsFlag();
  const hideFocTracker = !uscgFoc;
  const hideAorsTab = !useAorTabFlag();
  const hubOnly = useHubOnlyFlag();
  const aorText = useAorText();
  const hubNavItems = getHubNavItems({
    hideNotifications,
    hideActions,
    hideContactLists,
    hideReviewQueue,
    hideFocTracker,
    hideAorsTab,
    aorPlural: aorText('AORs'),
  });
  const currentlySelected = useSelector(
    (state: any) => state.app.currentlySelectedGroup as Group
  );
  const orgDisplayName = useDisplayGroupName(currentlySelected.group_name);

  const isOrgAdmin = useIsOrgAdmin();
  const onboarding = useOrgOnboardingProgress({ enabled: isOrgAdmin });
  const { lockIncident, utilityImpactLevel } = useSubscriptionLevel();
  const { close } = useSidebarNavContext();
  const [query, setQuery] = useState('');

  const visibleHubItems = useMemo(
    () => filterNavItemsByQuery(hubNavItems, query),
    [hubNavItems, query]
  );
  const showOrgFiles = matchesNavQuery('Org Files', query);
  const hubSectionVisible = visibleHubItems.length > 0 || showOrgFiles;
  const searching = query.trim().length > 0;
  const noHubMatches = searching && !hubSectionVisible;

  return (
    <nav aria-label="primary sidebar" className="tw:flex tw:flex-col tw:gap-1">
      <div className="tw:flex tw:items-center tw:gap-2 tw:px-4 tw:pt-5 tw:pb-2 tw:text-base tw:font-semibold">
        <div className="avatar-initials sml">{getInitials(orgDisplayName)}</div>
        <div className="tw:min-w-0 tw:flex-1 tw:break-words">
          {orgDisplayName}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label="Close menu"
          onClick={close}
        >
          <X className="tw:size-4" />
        </Button>
      </div>

      <SidebarNavSearchInput value={query} onChange={setQuery} />

      {sitrepLandingFlag && !searching && (
        <SidebarNavSection title="PRATUS AI SITREPS">
          <ListGroupNavItem
            to="/sitreps"
            icon="library_books"
            label="PRATUS AI SITREPS"
          />
        </SidebarNavSection>
      )}

      {!searching && (
        <SidebarNavSection title="Product Tours">
          <ListGroupButtonItem
            icon="tour"
            label="Product Tours"
            onClick={() => setSecondPanel('tours')}
          />
        </SidebarNavSection>
      )}

      {!searching && isOrgAdmin && !user?.external_user && (
        <SidebarNavSection title="Organization setup">
          <ListGroupNavItem
            to={ONBOARDING_HREF}
            iconNode={<ListChecks className="tw:size-4" />}
            label={
              onboarding.state?.completed_at
                ? 'Organization setup'
                : `Organization setup · ${onboarding.progress.label}`
            }
            isActive={(location) =>
              new URLSearchParams(location.search).has(ONBOARDING_PARAM)
            }
          />
        </SidebarNavSection>
      )}

      {hubSectionVisible ? (
        <SidebarNavSection title="Hub">
          {visibleHubItems.map((item) => {
            const Icon = item.icon;
            return (
              <ListGroupNavItem
                key={item.tab}
                to={item.to}
                iconNode={<Icon className="tw:size-4" />}
                label={item.label}
                isDisabled={user?.external_user}
                isActive={(location) =>
                  isHubNavItemActive(
                    item.tab,
                    location.pathname,
                    location.search,
                    hideNotifications
                  )
                }
              />
            );
          })}
          {showOrgFiles ? (
            <ListGroupNavItem
              to="/org_files"
              iconNode={<Folder className="tw:size-4" />}
              label="Org Files"
            />
          ) : null}
        </SidebarNavSection>
      ) : null}

      {noHubMatches ? (
        <p className="tw:px-3 tw:py-2 tw:text-xs tw:text-muted-foreground">
          No matching navigation
        </p>
      ) : null}

      {!searching && (
        <SidebarNavSection title="Incident Management">
          <ListGroupButtonItem
            icon="problem"
            label="Incident Management"
            onClick={() => setSecondPanel('ics')}
            isLocked={lockIncident}
            lockTooltip={hubOnly ? UPGRADE_TO_ENABLE : undefined}
          />
        </SidebarNavSection>
      )}
      {!searching && velcoFlag && utilityImpactLevel !== 'None' && (
        <SidebarNavSection title="Weather">
          <ListGroupNavItem
            to="/utility_impact_prediction"
            icon="report"
            label="Utility Impact Prediction"
          />
        </SidebarNavSection>
      )}
      {!searching && (
        <SidebarNavSection title="Settings">
          <ListGroupNavItem
            to="/groups"
            icon="settings"
            label="Org Settings"
            isDisabled={user?.external_user}
          />
        </SidebarNavSection>
      )}
    </nav>
  );
};

export default MainNavPanel;
